import { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
	Alert,
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { getAllAttrConfigs, getAttrOptions, putAttrConfig } from "./api";

// ─── BQ Schema Reference Panel ───────────────────────────────────────────────
const BQ_PKEY_FIELDS = [
	{ field: "BRAND", source: "ITEM_MASTER.BRAND", note: "Always included — part of PKEY" },
	{ field: "DEPARTMENT", source: "ITEM_MASTER.DEPARTMENT", note: "= Category — always included in PKEY" },
	{ field: "COLOR", source: "ITEM_MASTER.COLOR", note: "Always included — part of PKEY" },
	{ field: "PALLETE_TYPE", source: "ITEM_MASTER.PALLETE_TYPE", note: "Always included — part of PKEY" },
	{ field: "SIZE", source: "ITEM_MASTER.SIZE", note: "Size dimension — always fetched" },
	{ field: "BARCODE", source: "ITEM_MASTER.BARCODE", note: "Joins to sales data" },
];

const BqSchemaPanel = ({ attrOptions }) => (
	<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, background: "#f9fafb" }}>
		<Typography sx={{ fontWeight: 700, fontSize: "0.82rem", mb: 1 }}>
			BigQuery Schema Mapping
		</Typography>
		<Typography sx={{ fontSize: "0.72rem", color: "#6b7280", mb: 1.5 }}>
			The PKEY (<code>BRAND-DEPARTMENT-COLOR-PALLETE_TYPE</code>) is fixed and always used to look up sales in BigQuery.
			Configurable attributes (fashionability, occasion, sleeve, sub_cat, sub_brand) exist in <strong>both Relook and BigQuery ITEM_MASTER</strong> — they are used to match Relook options across seasons. The BQ columns are fetched for reference and future BQ-side filtering.
		</Typography>

		{/* Fixed PKEY fields */}
		<Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1d4ed8", mb: 0.5 }}>Fixed BigQuery PKEY Fields</Typography>
		<TableContainer sx={{ mb: 2 }}>
			<Table size="small">
				<TableHead>
					<TableRow sx={{ backgroundColor: "#eff6ff" }}>
						<TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, py: 0.5 }}>BQ Field</TableCell>
						<TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, py: 0.5 }}>Source Column</TableCell>
						<TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, py: 0.5 }}>Note</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{BQ_PKEY_FIELDS.map((row) => (
						<TableRow key={row.field}>
							<TableCell sx={{ fontSize: "0.68rem", py: 0.4, fontFamily: "monospace", color: "#1d4ed8" }}>{row.field}</TableCell>
							<TableCell sx={{ fontSize: "0.68rem", py: 0.4, fontFamily: "monospace" }}>{row.source}</TableCell>
							<TableCell sx={{ fontSize: "0.68rem", py: 0.4, color: "#6b7280" }}>{row.note}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>

		{/* Configurable matching attributes */}
		<Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#059669", mb: 0.5 }}>Configurable Relook Matching Attributes</Typography>
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow sx={{ backgroundColor: "#f0fdf4" }}>
						<TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, py: 0.5 }}>Attribute Key</TableCell>
						<TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, py: 0.5 }}>Relook Column</TableCell>
						<TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, py: 0.5 }}>BQ Column</TableCell>
						<TableCell sx={{ fontSize: "0.68rem", fontWeight: 700, py: 0.5 }}>Description</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{attrOptions.map((a) => (
						<TableRow key={a.key}>
							<TableCell sx={{ fontSize: "0.68rem", py: 0.4, fontFamily: "monospace", fontWeight: 600 }}>{a.key}</TableCell>
							<TableCell sx={{ fontSize: "0.68rem", py: 0.4, fontFamily: "monospace", color: "#374151" }}>{a.relook_column}</TableCell>
							<TableCell sx={{ fontSize: "0.68rem", py: 0.4, fontFamily: "monospace", color: "#1d4ed8" }}>
								{a.bq_column}
							</TableCell>
							<TableCell sx={{ fontSize: "0.68rem", py: 0.4, color: "#6b7280" }}>{a.description}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	</Paper>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const AttrConfigDrawer = ({ open, onClose, onSaved }) => {
	const [attrOptions, setAttrOptions] = useState([]);
	const [configs, setConfigs] = useState([]); // all brand+category rows from Mongo
	const [dirtyMap, setDirtyMap] = useState({}); // {brand|category: [attrs]}
	const [savingKey, setSavingKey] = useState(null);
	const [savedKeys, setSavedKeys] = useState(new Set());
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!open) return;
		let mounted = true;
		const load = async () => {
			try {
				setLoading(true);
				setError("");
				setDirtyMap({});
				setSavedKeys(new Set());
				const [opts, allConfigs] = await Promise.all([getAttrOptions(), getAllAttrConfigs()]);
				if (!mounted) return;
				setAttrOptions(opts || []);
				setConfigs(allConfigs || []);
			} catch (err) {
				if (!mounted) return;
				setError(err?.response?.data?.message || "Unable to load configs");
			} finally {
				if (mounted) setLoading(false);
			}
		};
		load();
		return () => { mounted = false; };
	}, [open]);

	const getAttrs = (brand, category) => {
		const key = `${brand}|${category}`;
		if (dirtyMap[key]) return dirtyMap[key];
		const row = configs.find((c) => c.brand === brand && c.category === category);
		return row?.attributes || [];
	};

	const toggleAttr = (brand, category, attrKey) => {
		const key = `${brand}|${category}`;
		const current = getAttrs(brand, category);
		const next = current.includes(attrKey) ? current.filter((a) => a !== attrKey) : [...current, attrKey];
		setDirtyMap((prev) => ({ ...prev, [key]: next }));
	};

	const saveRow = async (brand, category) => {
		const key = `${brand}|${category}`;
		const attrs = getAttrs(brand, category);
		try {
			setSavingKey(key);
			setError("");
			const user = localStorage.getItem("user_email") || "system";
			await putAttrConfig({ brand, category, attributes: attrs, updatedBy: user });
			setSavedKeys((prev) => new Set([...prev, key]));
			// update local configs state
			setConfigs((prev) =>
				prev.map((c) => (c.brand === brand && c.category === category ? { ...c, attributes: attrs } : c))
			);
			onSaved?.();
		} catch (err) {
			setError(err?.response?.data?.message || `Unable to save ${brand}/${category}`);
		} finally {
			setSavingKey(null);
		}
	};

	const isDirty = (brand, category) => {
		const key = `${brand}|${category}`;
		return Boolean(dirtyMap[key]);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Box>
						<Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Attribute Configuration</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
							Select which Relook attributes to use when matching options across seasons per brand &amp; category.
						</Typography>
					</Box>
				</Stack>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{ p: 2 }}>
				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				{loading ? (
					<Stack alignItems="center" py={6}><CircularProgress /></Stack>
				) : (
					<Stack gap={3}>
						{/* ── Config Table ── */}
						<Box>
							<Typography sx={{ fontWeight: 700, fontSize: "0.85rem", mb: 1 }}>
								Brand × Category Attribute Config ({configs.length} combinations)
							</Typography>
							{configs.length === 0 ? (
								<Alert severity="info">No configs found in MongoDB. Run a report first — configs are auto-created per brand &amp; category.</Alert>
							) : (
								<TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", backgroundColor: "#f3f4f6", whiteSpace: "nowrap", minWidth: 120 }}>
													Brand
												</TableCell>
												<TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", backgroundColor: "#f3f4f6", whiteSpace: "nowrap", minWidth: 120 }}>
													Category
												</TableCell>
												{attrOptions.map((a) => (
													<TableCell
														key={a.key}
														sx={{ fontWeight: 700, fontSize: "0.68rem", backgroundColor: "#f3f4f6", textAlign: "center", whiteSpace: "nowrap" }}>
														<Tooltip
															title={
																<Box>
																	<div><b>{a.label}</b></div>
																	<div>Relook: <code>{a.relook_column}</code></div>
																	<div>BQ: <code>{a.bq_column}</code></div>
																	<div style={{ marginTop: 4, color: "#d1fae5" }}>{a.description}</div>
																</Box>
															}
															placement="top"
															arrow>
															<Stack direction="row" alignItems="center" justifyContent="center" gap={0.3} sx={{ cursor: "help" }}>
																{a.label}
																<InfoOutlinedIcon sx={{ fontSize: "0.75rem", color: "#9ca3af" }} />
															</Stack>
														</Tooltip>
													</TableCell>
												))}
												<TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", backgroundColor: "#f3f4f6", textAlign: "center", minWidth: 80 }}>
													Active
												</TableCell>
												<TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", backgroundColor: "#f3f4f6", textAlign: "center", minWidth: 70 }}>
													Save
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{configs.map((row) => {
												const { brand, category } = row;
												const key = `${brand}|${category}`;
												const currentAttrs = getAttrs(brand, category);
												const isSaving = savingKey === key;
												const isSaved = savedKeys.has(key);
												const dirty = isDirty(brand, category);

												return (
													<TableRow
														key={key}
														sx={{ backgroundColor: dirty ? "rgba(245,158,11,0.05)" : "transparent" }}>
														<TableCell sx={{ fontSize: "0.72rem", fontWeight: 600 }}>{brand}</TableCell>
														<TableCell sx={{ fontSize: "0.72rem" }}>{category}</TableCell>
														{attrOptions.map((a) => (
															<TableCell key={a.key} sx={{ textAlign: "center", py: 0.2 }}>
																<Checkbox
																	size="small"
																	checked={currentAttrs.includes(a.key)}
																	onChange={() => toggleAttr(brand, category, a.key)}
																	sx={{ p: 0.3 }}
																/>
															</TableCell>
														))}
														<TableCell sx={{ textAlign: "center" }}>
															<Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="center">
																{currentAttrs.map((a) => (
																	<Chip
																		key={a}
																		label={a.replace(/_/g, " ")}
																		size="small"
																		sx={{ fontSize: "0.6rem", height: 18 }}
																	/>
																))}
															</Stack>
														</TableCell>
														<TableCell sx={{ textAlign: "center" }}>
															{isSaving ? (
																<CircularProgress size={16} />
															) : isSaved && !dirty ? (
																<Tooltip title="Saved"><CheckCircleIcon sx={{ color: "#059669", fontSize: "1.1rem" }} /></Tooltip>
															) : (
																<Tooltip title={dirty ? "Save changes" : "No changes"}>
																	<span>
																		<IconButton
																			size="small"
																			disabled={!dirty || currentAttrs.length === 0}
																			onClick={() => saveRow(brand, category)}
																			sx={{ color: dirty ? "#1d4ed8" : "#d1d5db" }}>
																			<SaveIcon fontSize="small" />
																		</IconButton>
																	</span>
																</Tooltip>
															)}
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</Box>

						<Divider />

						{/* ── BQ Schema Reference ── */}
						<BqSchemaPanel attrOptions={attrOptions} />
					</Stack>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default AttrConfigDrawer;
